import React, { useState,useRef, useEffect } from "react";
import { useWindowSize } from '@att-bit/duc.hooks.use-window-size';

export enum CardShellVariant {
  DEFAULT = "default",
  BUTTON = "button",
  CHECKBOX = "checkbox",
  LINK = "link",
  RADIO = "radio"
}

export enum CardShellBgColor {
  WHITE = "bg-white",
  GRAY800 = "bg-gray-800",
  GRAY200 = "bg-gray-200",
  GRAY100 = "bg-gray-100",
  ATTBLUE000 = "bg-att-blue-000"
}

export type CardshellProps = {
  /** Determines background color of card */
  bgColor?: CardShellBgColor;
  /** children (text/jsx) to be rendered in component  */
  children?: React.ReactNode | string;
  /** Determines whether the card is disabled */
  disabled?: boolean;
  /** Sets an href for the card */
  href?: string;
  /** Determines if CardShell is "checked" */
  isChecked?: boolean;
  /** remove focus even if the card is ,checked by default using isChecked props */
  removeFocus?: boolean;
  /** Determines whether the Card is on a dark background */
  onDark?: boolean;
  /** Determines type of CardShell (default sets no defined role) */
  variant?: CardShellVariant;
  /** Determines if CardShell should have full bleed on mobile (MUST BE USED IN PARALLEL WITH CARDSHELLROW) */
  withMobileBleed?: boolean;
  /** sets corner radius class to radius-md (used when a card is nested within another card) */
  nested?: Boolean;
  /** If true, adds a 10% transparent cobalt background when selected */
  withSelectedBg?: boolean;
  /** Event that is triggered when the CardShell is clicked */
  onClick?: (value: boolean | undefined) => void;
   /** Event that is triggered when pressed on Arrow Right.  */
  onRightKeyPress?:any,
   /** Event that is triggered when pressed on Arrow Left . */
  onLeftKeyPress?:any,
   /** removes the zindex so popover doesn't on top of two cardshells*/
  removeZIndex?: boolean,
   /** Determines theme for card BASE, NEUTRAL, ACCENT, DARKACCENT, LIGHTIMAGE, DARKIMAGE */
  theme?: any;
  /** removes the rel global class so popover doesn't overlap with footer fields*/
  removeRelClass?: boolean,
  /** if animation needed*/
  animation?: boolean,
  /** which direction the component should animate- right or left or up or down*/
  animationDirection?: string
};

export const Cardshell = ({
  bgColor,
  children,
  disabled = false,
  href,
  isChecked,
  removeFocus = false,
  onClick,
  onDark = false,
  target,
  variant = CardShellVariant.DEFAULT,
  withMobileBleed,
  withSelectedBg,
  nested,
  removeZIndex = false,
  removeRelClass = false,
  theme = "theme-base-bg",
  animation = false,
  animationDirection = "right",
  ...props
}: CardshellProps &
  React.HTMLAttributes<HTMLElement> &
  React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
  const [checked, setChecked] = useState(
    disabled ? false : isChecked ? isChecked : false
  );
  const divRef = useRef<any>(null);
  useEffect(() => {
    if (!disabled) {
      setChecked(isChecked);
      if(isChecked && !removeFocus){
        divRef.current.focus();
      }
    }
  }, [isChecked, removeFocus]);
  
  const windowSize = useWindowSize();
  const isMobile = windowSize?.mobile;

  const onKeyDown = (
    e: React.KeyboardEvent<
      HTMLDivElement | HTMLButtonElement | HTMLAnchorElement
    >
  ) => {
    if (!disabled) {
      const { key } = e;
      if ((key === "Enter" || key === " ") && variant !== "default" && (e.target === divRef.current)) {
        e.preventDefault();
        if (variant === "radio" || variant === "checkbox") {
          setChecked(prevState => {
            if (onClick) {
              onClick(variant === "checkbox" ? !prevState : true);
            }
            return variant === "checkbox" ? !prevState : true;
          });
        }
        if (!(variant === "radio" || variant === "checkbox") && onClick) {
          onClick(undefined);
        }
      }
      if (variant === "radio" ){
        if (key === "ArrowRight"  ||key === "ArrowDown"){
          props && props?.onRightKeyPress();
          
        }
        else if(key === "ArrowLeft" || key === "ArrowDown"){
          props && props?.onLeftKeyPress();
        }
      }
   
    }

  };

  const onMouseDown = (e: React.MouseEvent) => {
    if (!disabled) {
      if (e.button === 0) {
        if (variant === "radio" || variant === "checkbox") {
          if ((e.target as Element).closest(".card.height-full") === divRef.current && !(e.target instanceof HTMLAnchorElement) && !(e.target instanceof HTMLButtonElement)) {
            setChecked(prevState => {
              if (onClick) {
                onClick(variant === "checkbox" ? !prevState : true);
              }
              return variant === "checkbox" ? !prevState : true;
            });
          }
        }
        if (!(variant === "radio" || variant === "checkbox") && onClick) {
          onClick(undefined);
        }
      }
    }
  };

  const addSelectedClassName = () => {
    if (disabled) {
      return "";
    }
    const selectedbg = withSelectedBg ? 
                          (theme === "theme-base-bg") ? " bg-att-blue-000" : ` ${bgColor}`
                       : "";
    const selectedOption =
      (variant === "radio" || variant === "checkbox") && checked;
    return selectedOption ? ` selected${selectedbg}` : "";
  };

  const Element = variant === "link" ? "a" : "div";

  const propsClassNames = ` ${props.className}` || "";
  delete props.className;

  return (
    <Element
      aria-checked={
        variant === "checkbox" || variant === "radio"
          ? checked ? true : false : undefined
      }
      ref={divRef}
      aria-disabled={variant !== "default" ? disabled : undefined}
      onKeyDown={onKeyDown}
      onMouseDown={onMouseDown}
      tabIndex={variant !== "default" ? (disabled ? -1 : 0) : null}
      role={
        variant === "link"
          ? undefined
          : variant !== "default"
          ? variant
          : "region"
      }
      {...(variant === "link"
        ? {
            href: href && disabled !== true ? href : null,
            target: target ? target : "_self"
          }
        : {})}
      {...props}
      className={`card height-full ${!removeRelClass ? "rel" : ''} ${!removeZIndex ? "z0" : ''} pad-sm-lg pad-md-md ${
        variant === "default" || variant === "link" || variant === "button"
          ? theme === "theme-base-bg"
            ? `${bgColor} bgcolor ${theme}${bgColor === "bg-white" || (theme === "theme-base-bg" && !(onDark || bgColor === "bg-gray-100" || bgColor === "bg-gray-200" || bgColor === "bg-gray-800" || bgColor === "bg-att-blue-000"))  ? " border-shadow" : ""}${onDark ? " bg-white" : ""}`
            : `${bgColor} bgcolor ${theme}`
          : onDark
          ? "bg-white"
          : "border-shadow"
      } ${
        (variant === "default" || variant === "link" || variant === "button") &&
        bgColor === "bg-gray-800" || theme === "theme-dark-accent-bg" || theme === "theme-dark-bg-img"
          ? "color-white"
          : "color-gray-800"
      }${addSelectedClassName()}${
        variant !== "default"
          ? ` block${variant === "link" ? " no-hover" : ""}`
          : ""
      } ${
        withMobileBleed
          ? `pad-t-xs-sm pad-r-lg-sm pad-b-xs-sm pad-l-lg-sm ${isMobile ? "" : "radius-lg"}`
          :(nested?
            "pad-xs-sm  radius-md"
            : "pad-xs-sm  radius-lg")
      } ${propsClassNames}
      ${animation ? "animated-container opacity0" : ""}
      `}
    data-animation-class={animation ? "slide-"+ animationDirection : undefined}      
    >
      {children} 
    </Element>
  );
};
